<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFormStore } from '@/stores/form'

const emit = defineEmits<{
  (e: 'submit'): void
}>()

const formStore = useFormStore()

const firstName = ref(formStore.data.firstName || '')
const lastName = ref(formStore.data.lastName || '')
const showValidation = ref(false)

const isValid = computed(() => {
  return firstName.value.trim().length > 0 && lastName.value.trim().length > 0
})

function handleSubmit() {
  if (!isValid.value) {
    showValidation.value = true
    return
  }

  formStore.setField('firstName', firstName.value.trim())
  formStore.setField('lastName', lastName.value.trim())
  emit('submit')
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleSubmit()
  }
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <div class="space-y-4">
        <!-- First Name -->
        <div>
          <input
            v-model="firstName"
            type="text"
            placeholder="First Name"
            autocomplete="given-name"
            @keydown="handleKeydown"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base transition-colors',
              showValidation && !firstName.trim()
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
          <p v-if="showValidation && !firstName.trim()" class="text-red-500 text-xs mt-1 ml-1">
            Please enter your first name
          </p>
        </div>

        <!-- Last Name -->
        <div>
          <input
            v-model="lastName"
            type="text"
            placeholder="Last Name"
            autocomplete="family-name"
            @keydown="handleKeydown"
            :class="[
              'w-full py-3.5 px-4 border-2 rounded-xl text-base transition-colors',
              showValidation && !lastName.trim()
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-primary'
            ]"
          />
          <p v-if="showValidation && !lastName.trim()" class="text-red-500 text-xs mt-1 ml-1">
            Please enter your last name
          </p>
        </div>
      </div>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        class="w-full mt-5 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all"
      >
        Continue
        <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
